import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { createPinia } from 'pinia';
import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import { createApp, markRaw } from 'vue';
import App from '@/app/App.vue';
import { router } from '@/router/router';
import {
  projectHistoryRefreshPlugin,
  sessionGuardPlugin,
} from '@/store/plugins';
import { zIndex } from '@/theme/zIndex';
import { icons } from '@/utils';
import { setDefaultZone } from '@/utils/helpers/datetime';
import { i18n } from './utils/i18n/vueI18n';
import '@vuepic/vue-datepicker/dist/main.css';
import 'prosemirror-view/style/prosemirror.css';
import '@/styles/animations.css';
import '@/styles/datatable.scss';
import '@/styles/header-icons.scss';

/**
 * Handle Vite Preload Error
 *
 * This happens when dynamic imports fail (usually after deployment when old chunks are deleted).
 * Vite recommends reloading to fetch fresh assets. The REAL fix is setting Cache-Control: no-cache on HTML.
 *
 * Issue: https://stackoverflow.com/questions/69300341/typeerror-failed-to-fetch-dynamically-imported-module-on-vue-vite-vanilla-set
 * Source: https://vite.dev/guide/build#load-error-handling
 */
window.addEventListener('vite:preloadError', (event) => {
  console.error('[DBG] vite_preload_error', event.payload);
  event.preventDefault();
  window.location.reload();
});

// A palette of the design tokens for the preset. The app's palettes stop at
// 900, and an unmapped step would fall back to Aura's own colour (emerald,
// slate), so 950 repeats 900.
function tokenPalette(name: string) {
  const steps = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

  return {
    ...Object.fromEntries(
      steps.map((step) => [step, `var(--color-${name}-${step})`]),
    ),
    950: `var(--color-${name}-900)`,
  };
}

// The PrimeVue theme on the design tokens of src/theme/theme.ts (WI 5799).
const beeboardPreset = definePreset(Aura, {
  primitive: {
    borderRadius: {
      none: '0',
      xs: 'var(--border-radius-2)',
      sm: 'var(--border-radius-4)',
      md: 'var(--border-radius-6)',
      lg: 'var(--border-radius-8)',
      xl: 'var(--border-radius-12)',
    },
  },
  semantic: {
    transitionDuration: 'var(--duration-base)',
    primary: tokenPalette('primary'),
    // The app's one focus ring (theme.ts, App.vue), also on form fields, where
    // Aura draws none.
    focusRing: {
      width: 'var(--focus-ring-width)',
      style: 'solid',
      color: 'var(--color-focus-ring)',
      offset: 'var(--focus-ring-offset)',
      shadow: 'none',
    },
    formField: {
      paddingX: 'var(--space-12)',
      paddingY: 'var(--space-8)',
      sm: {
        fontSize: 'var(--font-size-14)',
        paddingX: 'var(--space-10)',
        paddingY: 'var(--space-6)',
      },
      lg: { fontSize: 'var(--font-size-18)', paddingY: 'var(--space-10)' },
      focusRing: {
        width: '{focus.ring.width}',
        style: '{focus.ring.style}',
        color: '{focus.ring.color}',
        offset: '{focus.ring.offset}',
        shadow: '{focus.ring.shadow}',
      },
    },
    list: { optionGroup: { fontWeight: 'var(--font-weight-bold)' } },
    navigation: { submenuLabel: { fontWeight: 'var(--font-weight-bold)' } },
    mask: { transitionDuration: 'var(--duration-fast)' },
    // Popovers and option panels take the kit's radius for floating panels
    // (AppDropdown, UiSelect, UiContextMenu) instead of Aura's 6px.
    overlay: {
      select: { borderRadius: '{border.radius.lg}' },
      popover: { borderRadius: '{border.radius.lg}' },
    },
    colorScheme: {
      light: {
        // Text, borders and fills follow from the surfaces: text gray-700,
        // borders gray-200, form-field borders gray-300.
        surface: { 0: 'var(--color-gray-0)', ...tokenPalette('gray') },
        // The interactive primary: Aura's primary-500 is 4.44:1 on white, below AA.
        primary: {
          color: '{primary.600}',
          hoverColor: '{primary.700}',
          activeColor: '{primary.800}',
        },
        formField: {
          invalidBorderColor: 'var(--color-error-600)',
          invalidPlaceholderColor: 'var(--color-error-600)',
          shadow: 'var(--shadow-xs)',
        },
      },
    },
  },
  components: {
    card: {
      title: {
        fontSize: 'var(--font-size-20)',
        fontWeight: 'var(--font-weight-medium)',
      },
    },
    toast: {
      summary: {
        fontSize: 'var(--font-size-16)',
        fontWeight: 'var(--font-weight-medium)',
      },
      detail: {
        fontSize: 'var(--font-size-14)',
        fontWeight: 'var(--font-weight-medium)',
      },
    },
    // Aura insets the ring by 1px on table cells and rows and on tree nodes, so a
    // 2px ring would straddle the edge; the inset offset keeps it inside, as Aura
    // means it.
    datatable: {
      headerCell: { focusRing: { offset: 'var(--focus-ring-offset-inset)' } },
      row: { focusRing: { offset: 'var(--focus-ring-offset-inset)' } },
    },
    treetable: {
      headerCell: { focusRing: { offset: 'var(--focus-ring-offset-inset)' } },
      row: { focusRing: { offset: 'var(--focus-ring-offset-inset)' } },
    },
    tree: {
      node: { focusRing: { offset: 'var(--focus-ring-offset-inset)' } },
    },
  },
});

const app = createApp(App);
const store = createPinia();

setDefaultZone();

// add vue plugins
app.use(router);
app.use(icons);
app.use(store);
app.use(i18n);
app.use(PrimeVue, {
  theme: {
    preset: beeboardPreset,
    options: {
      darkModeSelector: false,
      // PrimeVue's styles sit in the CSS layer `primevue`, above `base` (the
      // resets and the focus ring, App.vue) and below every other app style. A
      // rule of the app wins over PrimeVue by the cascade, not by specificity or
      // load order, and so also over PrimeVue's hover and selected states: an
      // app rule that must leave such a state to PrimeVue restores it, as
      // datatable.scss does. PrimeVue 4.3 keeps three plain style blocks outside
      // the layer (base, baseicon, virtualscroller), which beat its own layered
      // rules: datatable.scss restores the table sort icons, and a table that
      // shows the virtual scroller's loader would need the same.
      cssLayer: { name: 'primevue', order: 'base, primevue' },
    },
  },
  // The z-index ladder of src/theme/zIndex.ts. The Toast, the only `modal`
  // user, has its own base above the confirm layer (NotificationLayer.vue).
  zIndex: {
    modal: zIndex.modal,
    overlay: zIndex.popover,
    menu: zIndex.popover,
    tooltip: zIndex.tooltip,
  },
});
app.use(ToastService);

// add pinia plugins
store.use((ctx) => {
  ctx.store.router = markRaw(router);
});
store.use(projectHistoryRefreshPlugin);
store.use(sessionGuardPlugin);

// Global error handlers to catch "silent death" issues
app.config.errorHandler = (err, _instance, info) => {
  console.error('[DBG] vue_global_error', err, info);
};

window.addEventListener('error', (e) => {
  console.error('[DBG] window_error', e.error || e.message);
});

window.addEventListener('unhandledrejection', (e) => {
  console.error('[DBG] unhandled_rejection', e.reason);
});

if (window.location.hostname.startsWith('bauwens')) {
  window.addEventListener('click', (event) => {
    console.log(
      `[DBG] click target: ${(event.target as HTMLElement).outerHTML}`,
    );
  });
}

// mount to dom node
app.mount('#app');
